/**
 * ObjectId template configuration for tracking events.
 *
 * Each template function receives all parameters from the tracking event
 * and returns a computed objectId string.
 *
 * Template functions are matched by the 'object' field in gnar-spec base events:
 * - page → superhumanFunnelPageBaseEvent
 * - button → superhumanFunnelButtonBaseEvent
 * - ctaButton → superhumanFunnelCtaBaseEvent
 * - component → superhumanFunnelComponentBaseEvent
 */

/**
 * Parameters available in all tracking events (from superhumanFunnelBaseEvent)
 */
interface BaseEventParams {
  pageId: string
  pageSlug: string
}

/**
 * Template function type - receives event params, returns objectId
 */
type TemplateFunction<T extends BaseEventParams> = (params: T) => string

/**
 * Sanitizes a string by removing all whitespace characters.
 * Used to ensure consistent objectId formatting across tracking events.
 */
const sanitize = (value: string): string => value.replaceAll(/\s+/g, '')

/**
 * Creates an objectId by sanitizing all values and joining them with underscores.
 * This wrapper ensures consistent formatting across all templates.
 *
 * @param values - Array of string values to sanitize and join
 * @returns Sanitized objectId string with values joined by underscores
 *
 * @example
 * createObjectId(['pricing page', 'Sign Up']) // Returns: 'pricingpage_SignUp'
 */
const createObjectId = (...values: string[]): string => values.map(sanitize).join('_')

/**
 * Template for page events (page-show, page-redirect)
 */
const pageTemplate: TemplateFunction<BaseEventParams> = ({ pageSlug }) => createObjectId(pageSlug)

/**
 * Template for generic button events (button-click)
 */
const buttonTemplate: TemplateFunction<
  BaseEventParams & {
    displayContent: string
  }
> = ({ pageSlug, displayContent }) => createObjectId(pageSlug, displayContent)

/**
 * Template for CTA button events (ctaButton-click)
 */
const ctaButtonTemplate: TemplateFunction<
  BaseEventParams & {
    ctaType: string
    displayContent: string
  }
> = ({ pageSlug, ctaType }) => createObjectId(pageSlug, ctaType)

/**
 * Template for component events (component-show)
 * Pattern: pageId_componentType_componentId
 * Example: "homepage_Header_main-nav"
 */
const componentTemplate: TemplateFunction<
  BaseEventParams & {
    componentId: string
    componentType: string
  }
> = ({ pageSlug, componentType, componentId }) =>
  createObjectId(pageSlug, componentType, componentId)

/**
 * ObjectId templates mapped by object type.
 *
 * The 'object' key must match the 'object' field defined in gnar-spec base events.
 */
export const objectIdTemplates = {
  page: pageTemplate,
  button: buttonTemplate,
  ctaButton: ctaButtonTemplate,
  component: componentTemplate,
} as const

/**
 * Default template used when no specific template is defined for an object type.
 * Pattern: pageId_object_action
 */
export const defaultObjectIdTemplate = (params: { pageSlug: string; object: string }): string =>
  createObjectId(params.pageSlug, params.object)
